import classNames from "classnames/bind";
import React, { type Dispatch, type SetStateAction } from "react";
import { Button } from "../../Button";
import { ButtonWithPopover } from "../../ButtonWithPopover";
import { DropdownMenu } from "../../DropdownMenu";
import { Icon, type IconProps } from "../../Icon";
import { getActionButtonSize } from "../DataTable.helpers";
import {
  type ActionsButtonProps,
  type ItemAction,
  type ItemBaseType,
} from "../types";
import styles from "./DataRowActions.module.scss";

const cx = classNames.bind(styles);

const DataRowActions = <ItemType extends ItemBaseType>({
  actions,
  density,
  className,
  handleOpen,
}: ActionsButtonProps<ItemType> & {
  className?: string;
  handleOpen?: Dispatch<SetStateAction<boolean>>;
}) => {
  // we want to only allow a max of 3 featured buttons in the row
  // and then use the elipsis menu for the remaining actions
  // only actions within this list will appear as a featured buttons
  const [primaryActions, overflowMenuActions] = actions.reduce<
    [ItemAction[], ItemAction[]]
  >(
    (actionBuckets, action) => {
      if (
        action.isOverflow ||
        actionBuckets[0].length >= 3 ||
        !action.buttonProps?.icon
      ) {
        actionBuckets[1].push(action);
      } else {
        actionBuckets[0].push(action);
      }

      return actionBuckets;
    },
    [[], []]
  );

  return (
    <div className={cx("container", className)}>
      {primaryActions
        ? primaryActions.map((action) => (
            <ButtonWithPopover
              key={action.label}
              label={action.label}
              buttonProps={{
                ...action.buttonProps,
                size: getActionButtonSize(density),
              }}
            />
          ))
        : null}
      {overflowMenuActions?.length > 0 ? (
        <DropdownMenu
          options={overflowMenuActions.map(({ label, buttonProps }) => ({
            label,
            ...(buttonProps?.icon
              ? {
                  slotLeft: ({
                    iconProps,
                  }: {
                    iconProps: Partial<IconProps>;
                  }) =>
                    buttonProps.icon ? (
                      <Icon
                        {...iconProps}
                        variant={buttonProps.icon}
                        {...(buttonProps?.iconProps ?? {})}
                      />
                    ) : null,
                }
              : {}),
            onClick: buttonProps?.onClick,
          }))}
          rootProps={{
            onOpenChange: (open) => {
              handleOpen?.(open);
            },
          }}
          contentProps={{
            align: "end",
          }}
        >
          <Button
            type="button"
            icon="dots-three-vertical"
            iconProps={{ size: 18 }}
            variant="outline"
            size={getActionButtonSize()}
            // data attribute is used to make sure click events on this trigger
            // don't register on handlers higher up the tree
            data-is-row-action-button
          />
        </DropdownMenu>
      ) : null}
    </div>
  );
};

export default DataRowActions;
